import React, { useState, useRef } from "react";
import { Modal } from "../shared-components/Modal";
import {
  useGetPersonExtractDocumentUploadUrl,
  useUploadFileToS3,
  useExtractDocumentInfo,
} from "../api/360FilesAPI";

interface ExtractFromFilesProps {
  open: boolean;
  onClose: () => void;
  person_uuid: string;
  item_type: string;
  onExtracted: (items: any[]) => void;
  title?: string;
}

export function ExtractFromFiles({
  open,
  onClose,
  person_uuid,
  item_type,
  onExtracted,
  title = "Extract from files",
}: ExtractFromFilesProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getUploadUrl = useGetPersonExtractDocumentUploadUrl();
  const uploadFileToS3 = useUploadFileToS3();
  const extractDocumentInfo = useExtractDocumentInfo();

  function clearFiles() {
    setFiles([]);
    setUploadProgress({});
    setUploadedFiles([]);
    setError(null);
  }

  async function handleFiles(selectedFiles: File[]) {
    setIsUploading(true);
    setError(null);
    setUploadProgress({});
    setUploadedFiles([]);
    for (const file of selectedFiles) {
      try {
        const { upload_url } = await getUploadUrl.mutateAsync({
          person_uuid: person_uuid ?? "",
          item_type: item_type ?? "",
          filename: file.name,
        });
        await uploadFileToS3.mutateAsync({ uploadUrl: upload_url, file });
        setUploadedFiles((prev) => [...prev, file.name]);
        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
      } catch (err: any) {
        setError(`Failed to upload ${file.name}: ${err?.message || err}`);
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
      }
    }
    setIsUploading(false);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!event.dataTransfer.files.length) return;
    const fileList = Array.from(event.dataTransfer.files);
    setFiles(fileList);
    handleFiles(fileList);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  function handleClick() {
    inputRef.current?.click();
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) return;
    const fileList = Array.from(e.target.files);
    setFiles(fileList);
    handleFiles(fileList);
  }

  function handleCancel() {
    clearFiles();
    onClose();
  }

  async function handleSubmit() {
    setIsExtracting(true);
    setError(null);
    try {
      let allExtracted: any[] = [];
      const extractPromises = uploadedFiles.map((filename) =>
        extractDocumentInfo.mutateAsync({
          person_uuid: person_uuid ?? "",
          item_type: item_type ?? "",
          filename,
        })
      );
      const extractResults = await Promise.all(extractPromises);
      for (const result of extractResults) {
        if (Array.isArray(result)) allExtracted = allExtracted.concat(result);
        else if (result?.items)
          allExtracted = allExtracted.concat(result.items);
        else if (result) allExtracted = allExtracted.concat(result);
      }
      onExtracted(allExtracted);
      clearFiles();
      onClose();
    } catch (err: any) {
      setError(`Extraction failed: ${err?.message || err}`);
      clearFiles();
    } finally {
      setIsExtracting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={title}
      width="w-full max-w-md"
      height="h-full"
    >
      <div className="pt-6">
        <div
          className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors hover:border-primary"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleClick}
          tabIndex={0}
          role="button"
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handleFileInputChange}
            aria-label="Upload file"
            multiple
          />
          <span className="text-sm text-muted-foreground">
            Drag & drop or <span className="underline">browse</span> to upload
          </span>
        </div>
        {files.length > 0 && (
          <div className="mt-2 text-xs text-gray-600">
            <div>Files to extract from:</div>
            <ul>
              {files.map((file, idx) => (
                <li key={file.name + idx} className="flex items-center gap-2">
                  <span>{file.name}</span>
                  {uploadProgress[file.name] !== undefined && (
                    <span className="ml-2">{uploadProgress[file.name]}%</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1 rounded bg-gray-200 text-gray-800 text-xs font-medium hover:bg-gray-300"
            onClick={handleCancel}
            disabled={isUploading || isExtracting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={isUploading || isExtracting || uploadedFiles.length === 0}
          >
            {isExtracting ? "Extracting..." : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ExtractFromFiles;
